import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CircleCheck, Layers, MailCheck, ShieldCheck } from 'lucide-react';
import { apiClient } from '../../api/client';
import { useI18n } from '../../i18n/I18nContext';
import { formatPhone } from '../../utils/datetime';
import AuthLayout from '../Login/AuthLayout';
import AuthField from '../Login/AuthField';
import AuthAlert from '../Login/AuthAlert';
import AuthSubmit from '../Login/AuthSubmit';
import { PHONE_RULE, normalizePhone, canAutoFocus, focusField } from '../Login/authHelpers';

const SUPPORT_EMAIL = 'support@darsly.uz';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The request goes to the public lead endpoint (POST /marketing/leads, no sign-in needed), where
 * staff see it in the admin panel's leads list. That DTO has a first name and a phone but no
 * email or company field, so those two travel in `learningPurpose`; `utmSource` marks the source.
 */
function sendAccessRequest({ name, email, phone, company }) {
  const note = ['CRM access request', `Email: ${email}`, company && `Company: ${company}`]
    .filter(Boolean)
    .join(' | ');
  return apiClient.post('/marketing/leads', {
    firstName: name.slice(0, 100),
    phoneNumber: phone,
    learningPurpose: note.slice(0, 1000),
    utmSource: 'crm-register',
  });
}

function Register() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [sentTo, setSentTo] = useState(null); // { email, phone } once the API accepted it
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    setError('');
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = t('auth.register.errorName');
    if (!EMAIL_RE.test(form.email.trim())) next.email = t('auth.register.errorEmail');
    if (!PHONE_RULE.test(normalizePhone(form.phone))) next.phone = t('auth.register.errorPhone');
    setFieldErrors(next);
    // the caret goes to the first field that needs attention
    const first = ['name', 'email', 'phone'].find((name) => next[name]);
    if (first) focusField(`register-${first}`);
    return !first;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validate()) return;
    const request = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: normalizePhone(form.phone),
      company: form.company.trim(),
    };
    setSubmitting(true);
    try {
      await sendAccessRequest(request);
      setSentTo({ email: request.email, phone: request.phone });
    } catch {
      // The success page is for requests the API accepted. A failure says so and where to write.
      setError(t('auth.register.errorDefault', { email: SUPPORT_EMAIL }));
    } finally {
      setSubmitting(false);
    }
  };

  const features = [
    { icon: <CircleCheck size={18} />, title: t('auth.register.feature1Title'), desc: t('auth.register.feature1Desc') },
    { icon: <Layers size={18} />, title: t('auth.register.feature2Title'), desc: t('auth.register.feature2Desc') },
    { icon: <ShieldCheck size={18} />, title: t('auth.register.feature3Title'), desc: t('auth.register.feature3Desc') },
  ];

  const signInLine = (
    <>
      {t('auth.register.alreadyHaveAccount')} <Link to="/login">{t('auth.register.signIn')}</Link>
    </>
  );

  return (
    <AuthLayout
      title={sentTo ? t('auth.register.successTitle') : t('auth.register.title')}
      tagline={t('auth.register.tagline1')}
      taglineEm={t('auth.register.taglineEm')}
      desc={t('auth.register.desc')}
      features={features}
      back={{ to: '/login', label: t('auth.register.backToSignIn') }}
      below={signInLine}
    >
      {sentTo ? (
        <div className="auth-state">
          <div className="auth-state-art" data-tone="success"><MailCheck aria-hidden="true" /></div>
          <div className="auth-form-header">
            <h1>{t('auth.register.successTitle')}</h1>
            <p>{t('auth.register.successSubtitle')}</p>
          </div>
          <div className="auth-success" role="status">
            {t('auth.register.successMessage', { phone: formatPhone(sentTo.phone), email: sentTo.email })}
          </div>
        </div>
      ) : (
        <>
          <div className="auth-form-header">
            <h1>{t('auth.register.title')}</h1>
            <p>{t('auth.register.subtitle')}</p>
          </div>

          <div className="auth-info-box">
            {t('auth.register.infoBoxBefore')} <strong>{t('auth.register.infoBoxStrong')}</strong> {t('auth.register.infoBoxAfter')}
          </div>

          <form onSubmit={handleSubmit} noValidate aria-busy={submitting}>
            <AuthField
              id="register-name"
              name="name"
              label={t('auth.register.nameLabel')}
              star
              required
              value={form.name}
              onChange={handleChange}
              placeholder={t('auth.register.namePlaceholder')}
              error={fieldErrors.name}
              autoComplete="name"
              autoFocus={canAutoFocus()}
              readOnly={submitting}
            />
            <AuthField
              id="register-email"
              name="email"
              type="email"
              label={t('auth.register.emailLabel')}
              star
              required
              value={form.email}
              onChange={handleChange}
              placeholder={t('auth.register.emailPlaceholder')}
              error={fieldErrors.email}
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              readOnly={submitting}
            />
            <AuthField
              id="register-phone"
              name="phone"
              type="tel"
              label={t('auth.register.phoneLabel')}
              star
              required
              value={form.phone}
              onChange={handleChange}
              placeholder={t('auth.register.phonePlaceholder')}
              error={fieldErrors.phone}
              inputMode="tel"
              autoComplete="tel"
              readOnly={submitting}
            />
            <AuthField
              id="register-company"
              name="company"
              label={t('auth.register.companyLabel')}
              value={form.company}
              onChange={handleChange}
              placeholder={t('auth.register.companyPlaceholder')}
              autoComplete="organization"
              readOnly={submitting}
            />

            <AuthAlert>{error}</AuthAlert>

            <AuthSubmit
              busy={submitting}
              label={t('auth.register.submit')}
              busyLabel={t('auth.register.sending')}
            />
          </form>
        </>
      )}
    </AuthLayout>
  );
}

export default Register;
